import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  confirmPasswordReset,
  getAuth,
  verifyPasswordResetCode,
} from '@angular/fire/auth';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { IonicModule, LoadingController } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SolInputComponent } from 'src/app/common/components/forms/sol-input/sol-input.component';
import { INPUT_TYPE } from 'src/app/common/components/forms/sol-input/sol-input.enums';
import {
  ALERT_TYPE,
  AlertService,
} from 'src/app/common/services/alert.service';

@Component({
  selector: 'sol-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  standalone: true,
  imports: [
    IonicModule,
    FormsModule,
    TranslateModule,
    SolInputComponent,
    CommonModule,
  ],
})
export class ResetPasswordComponent implements OnInit {
  inputType = INPUT_TYPE;

  oobCode: string | null = null;
  isCodeValid = false;

  password: string | null = '';
  confirmPassword: string | null = '';

  passwordValidations = {
    length: false,
    specialChar: false,
    numbers: false,
    uppercase: false,
    lowercase: false,
    match: false,
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private alertService: AlertService,
    private translate: TranslateService,
    private loadCtrl: LoadingController
  ) {}

  async ngOnInit() {
    this.oobCode = this.route.snapshot.queryParamMap.get('oobCode');
    try {
      const auth = getAuth();
      await verifyPasswordResetCode(auth, this.oobCode ?? '');
      this.isCodeValid = true;
    } catch {
      this.isCodeValid = false;
    }
  }

  validatePasswordRealtime() {
    const pwd = this.password ?? '';
    const confirm = this.confirmPassword ?? '';

    this.passwordValidations.length = pwd.length >= 8;
    this.passwordValidations.specialChar = /[@&#*!\/]/.test(pwd);
    this.passwordValidations.numbers = (pwd.match(/\d/g) || []).length >= 3;
    this.passwordValidations.uppercase = /[A-Z]/.test(pwd);
    this.passwordValidations.lowercase = /[a-z]/.test(pwd);
    this.passwordValidations.match = pwd === confirm;
  }

  isFormValid(): boolean {
    const v = this.passwordValidations;
    return (
      v.length &&
      v.specialChar &&
      v.numbers &&
      v.uppercase &&
      v.lowercase &&
      v.match
    );
  }

  async onSubmit() {
    if (!this.isFormValid()) {
      this.alertService.showAlert(
        ALERT_TYPE.ERROR,
        this.translate.instant('RESET_PASSWORD.errorNotRequirements'),
        3000
      );
      return;
    }

    const loader = await this.loadCtrl.create();
    await loader.present();

    try {
      const auth = getAuth();
      await confirmPasswordReset(auth, this.oobCode!, this.password ?? '');

      await this.alertService.showAlert(
        ALERT_TYPE.SUCCESS,
        this.translate.instant('RESET_PASSWORD.passwordResetSuccess'),
        4000,
        'bottom'
      );

      await this.router.navigate(['/login']);
    } catch {
      await this.alertService.showAlert(
        ALERT_TYPE.ERROR,
        this.translate.instant('COMMON.ALERTS.errorGeneric'),
        4000,
        'bottom'
      );
    } finally {
      loader.dismiss();
    }
  }
}
